import { Effect, ParseResult, Schema } from "effect";
//
// Timestamps
//
// Milliseconds
export const TimestampMsSchema = Schema.Number.annotations({
    title: "Timestamp Milliseconds",
    description: "Milliseconds since the Unix epoch"
}).pipe(Schema.brand("@open-revenue/schema/TimestampMs"));
const TimestampMsFromString = Schema.transformOrFail(Schema.String, TimestampMsSchema, {
    strict: true,
    decode: (string)=>Effect.gen(function*() {
            const milliseconds = parseInt(string, 10);
            if (isNaN(milliseconds)) {
                return yield* Effect.fail(new ParseResult.Forbidden(Schema.String.ast, "TimestampMs", "Invalid timestamp"));
            }
            return milliseconds;
        }),
    encode: (milliseconds)=>Effect.sync(()=>milliseconds.toString())
});
export const TimestampMs = {
    Schema: TimestampMsSchema,
    FromString: TimestampMsFromString,
    make: (milliseconds)=>milliseconds
};
// Seconds
export const TimestampSecondsSchema = Schema.Number.annotations({
    title: "Timestamp Seconds",
    description: "Seconds since the Unix epoch"
}).pipe(Schema.brand("@open-revenue/schema/TimestampSeconds"));
export const TimestampSeconds = {
    Schema: TimestampSecondsSchema,
    make: (seconds)=>seconds,
    fromDate: (date)=>Math.floor(date.getTime() / 1000),
    toDate: (seconds)=>new Date(seconds * 1000)
};
const TimestampSecondsFromString = Schema.transformOrFail(Schema.String, TimestampSecondsSchema, {
    strict: true,
    decode: (string)=>Effect.gen(function*() {
            const seconds = parseInt(string, 10);
            if (isNaN(seconds)) {
                return yield* Effect.fail(new ParseResult.Forbidden(Schema.String.ast, "TimestampSeconds", "Invalid timestamp"));
            }
            return seconds;
        }),
    encode: (seconds)=>Effect.sync(()=>seconds.toString())
});
export const Timestamp = {
    Secs: {
        Schema: TimestampSecondsSchema,
        FromString: TimestampSecondsFromString,
        Type: TimestampSecondsSchema.Type,
        fake: ()=>Math.floor(new Date().getTime() + Math.floor(Math.random() * 3600 * 24 * 1000) * (Math.random() > 0.5 ? 1 : -1) / 1000),
        make: (seconds)=>seconds,
        fromDate: (date)=>Math.floor(date.getTime() / 1000),
        fromMillis: (milliseconds)=>Math.floor(milliseconds / 1000),
        toDate: (seconds)=>new Date(seconds * 1000),
        toMillis: (seconds)=>seconds * 1000
    },
    Ms: {
        Schema: TimestampMsSchema,
        FromString: TimestampMsFromString,
        Type: TimestampMsSchema.Type,
        fake: ()=>new Date().getTime() + Math.floor(Math.random() * 3600 * 24 * 1000) * (Math.random() > 0.5 ? 1 : -1),
        make: (milliseconds)=>milliseconds,
        fromSeconds: (seconds)=>seconds * 1000,
        toSeconds: (milliseconds)=>Math.floor(milliseconds / 1000),
        fromDate: (date)=>Math.floor(date.getTime()),
        toDate: (milliseconds)=>new Date(milliseconds)
    }
};
//
const HumanReadableSlashDateSchema = Schema.String.pipe(Schema.pattern(/^\d{1,2}\/\d{1,2}\/\d{4}$/));
const DateStringSchema = Schema.String.pipe(Schema.pattern(/^\d{4}-\d{2}-\d{2}$/), Schema.annotations({
    title: "Date String",
    description: "A date string in the format YYYY-MM-DD"
}), Schema.brand("@open-revenue/schema/DateString"));
const dateToStartOfDay = (date)=>{
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
};
const DateSecondsSchema = Schema.Number.pipe(Schema.annotations({
    title: "Date Seconds",
    description: "The number of seconds since the Unix epoch, normalized to the start of the day",
    examples: [
        1714857600
    ]
}), Schema.brand("@open-revenue/schema/DateSeconds"));
// Takes in a string matching 8/12/2024 and converts it to 2024-08-12
const DateStringFromHumanReadableDateSchema = Schema.transform(HumanReadableSlashDateSchema, DateStringSchema, {
    strict: true,
    decode: (humanReadableDate)=>{
        let [month, day, year] = humanReadableDate.split("/").map(Number);
        if (year < 100) {
            year += 2000;
        }
        return `${year}-${month < 10 ? "0" : ""}${month}-${day < 10 ? "0" : ""}${day}`;
    },
    encode: (dateString)=>{
        const [year, month, day] = dateString.split("-").map(Number);
        return `${month}/${day}/${year}`;
    }
});
export const DateStringFromHumanReadableDate = {
    Schema: DateStringFromHumanReadableDateSchema,
    make: (humanReadableDate)=>Schema.decodeUnknown(DateStringFromHumanReadableDateSchema)(humanReadableDate),
    makeUnsafe: (dateString)=>dateString,
    fromDate: (date)=>date.toISOString().split("T")[0],
    toDate: (dateString)=>new Date(dateString)
};
export const DateString = {
    Schema: DateStringSchema,
    FromHumanReadbleSlash: DateStringFromHumanReadableDateSchema,
    makeUnsafe: (dateString)=>dateString,
    fromDate: (date)=>date.toISOString().split("T")[0],
    toDate: (dateString)=>new Date(dateString)
};
export const DateType = {
    Seconds: {
        Schema: DateSecondsSchema,
        Type: DateSecondsSchema.Type,
        makeUnsafe: (dateSeconds)=>dateSeconds,
        make: (dateSeconds)=>Schema.decodeUnknown(DateSecondsSchema)(dateSeconds),
        fromDate: (date)=>Math.floor(dateToStartOfDay(date).getTime() / 1000),
        fromDateString: (dateString)=>Schema.decodeUnknown(DateSecondsSchema)(dateString),
        fromDateStringUnsafe: (dateString)=>new Date(dateString).getTime() / 1000,
        toDate: (dateSeconds)=>new Date(dateSeconds * 1000),
        fake: ()=>Math.floor(Date.now() / 1000)
    },
    String: {
        Schema: DateStringSchema,
        Type: DateStringSchema.Type,
        makeUnsafe: (dateString)=>dateString,
        make: (dateString)=>Schema.decodeUnknown(DateStringSchema)(dateString),
        fromDate: (date)=>date.toISOString().split("T")[0],
        fromDateSeconds: (dateSeconds)=>Schema.decodeUnknown(DateStringSchema)(dateSeconds),
        fromDateSecondsUnsafe: (dateSeconds)=>new Date(dateSeconds * 1000).toISOString().split("T")[0],
        fake: ()=>DateString.fromDate(new Date())
    }
};
